import { Mail, MapPin, Clock, Sparkles } from "lucide-react";
import { motion } from "framer-motion";

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const ContactHero = () => {
  const cards = [
    {
      icon: <Mail className="w-6 h-6" />,
      title: "Email Us",
      subtitle: "For general inquiries",
      chip: "from-clay-100 to-clay-200 text-clay-600",
      bar: "from-clay-500 to-clay-600",
      content: (
        <a
          href="mailto:tantikacustomercare@outlook.com"
          className="text-clay-600 font-medium hover:text-clay-700 block truncate"
          title="tantikacustomercare@outlook.com"
        >
          tantikacustomercare@outlook.com
        </a>
      ),
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "Visit Us",
      subtitle: "Kolkata, Bengal",
      chip: "from-saffron-100 to-saffron-200 text-saffron-700",
      bar: "from-saffron-500 to-saffron-600",
      content: (
        <div className="text-cocoa-700 text-sm mt-2 space-y-1">
          <div>Kolkata</div>
          <div>Bengal, India</div>
        </div>
      ),
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Business Hours",
      subtitle: "We're here to help",
      chip: "from-green-100 to-green-200 text-green-700",
      bar: "from-green-500 to-green-600",
      content: (
        <div className="text-cocoa-700 text-sm mt-2 space-y-1">
          <div>Mon-Fri: 9AM - 6PM</div>
          <div>Saturday: 10AM - 4PM</div>
          <div className="text-red-500">Sunday: Closed</div>
        </div>
      ),
    },
  ];

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-cream-100 via-cream-50 to-white py-16 md:py-24">
      {/* Decorative blobs */}
      <div className="absolute -top-24 -left-24 w-96 h-96 bg-clay-200/40 rounded-full blur-3xl animate-pulse-soft"></div>
      <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-saffron-200/40 rounded-full blur-3xl animate-pulse-soft"></div>

      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.3 }}
        variants={fadeInUp}
        className="relative container mx-auto px-4"
      >
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/70 backdrop-blur-md text-clay-600 border border-cream-200 rounded-full mb-6 shadow-sm">
            <Sparkles className="w-4 h-4" />
            Get in Touch
          </span>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight text-cocoa-900">
            Contact{" "}
            <span className="font-bengali text-gradient-warm">তন্তিকা</span>
          </h1>

          <p className="text-lg md:text-xl text-cocoa-600 mb-10 max-w-3xl mx-auto">
            Have questions about our products, want to collaborate with
            artisans, or need assistance with an order? We're here to help!
          </p>

          {/* Contact Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto mt-8">
            {cards.map((card, index) => (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative overflow-hidden bg-white p-6 rounded-2xl shadow-sm border border-cream-200 hover:shadow-warm hover:-translate-y-1 transition-all duration-300 flex flex-col items-center text-center"
              >
                <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${card.bar}`}></div>
                <div className={`inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br ${card.chip} rounded-xl mb-4`}>
                  {card.icon}
                </div>
                <h3 className="font-bold text-cocoa-900 mb-1">{card.title}</h3>
                <p className="text-cocoa-500 text-sm">{card.subtitle}</p>
                <div className="mt-2 w-full px-2">{card.content}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default ContactHero;
